import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Spinner, Nav, NavItem, NavLink } from "reactstrap";
import { useEmployeeHistory } from "../hooks/useEmployeeHistory";
import { METRIC_LABELS, DEFAULT_WEIGHTS } from "../../../../constants/performance";

/**
 * Labels curtas (1 palavra) para as tabs de métricas.
 * Usamos as labels completas no tooltip/detalhes e as curtas nos botões.
 */
const SHORT_LABELS = {
    trialConversion: "Conversão",
    attendanceRate: "Presença",
    assiduidade: "Assiduidade",
    carteiraHealth: "Carteira",
    evaluationCoverage: "Cob. Avaliação",
    ocupacaoTurmas: "Ocupação",
    revenue: "Faturamento",
    salesNew: "Vendas",
    salesRenewal: "Renovações",
    conversionRate: "Conversão",
    trialScheduled: "Experimentais",
    tasksOnTime: "Tarefas",
    ticketMedio: "Ticket",
    crescimentoLiquido: "Crescimento",
    churnRate: "Churn",
    salesCount: "Vendas",
    cancellations: "Cancelamentos",
    activeMembers: "Alunos",
    renewalRate: "Renovação",
};

/**
 * Gráfico de evolução de linha SVG premium, idêntico aos gráficos do dashboard gerencial.
 * Suporta curvas bezier suaves (smooth), hover interativo com linha tracejada e tooltip flutuante.
 */
const EvolutionLineChart = ({ data, color = "#e2661c", height = 220 }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);

    if (!data || data.length === 0) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5" style={{ height }}>
                <i className="mdi mdi-chart-box-outline font-size-24 mb-2 opacity-50" style={{ color: "#784b22" }} />
                <span className="font-size-12 fw-bold opacity-75" style={{ color: "#784b22" }}>Sem dados históricos suficientes...</span>
            </div>
        );
    }

    const values = data.map(d => d.value);
    const maxVal = Math.max(...values, 0.1);
    const minVal = Math.min(...values, 0);
    const range = maxVal - minVal || 1;

    // Mapeia os pontos escalando para encaixar entre 10% e 85% da altura para não cortar elementos superiores
    const points = data.map((item, idx) => {
        const pct = (item.value - minVal) / range;
        return {
            x: (100 / Math.max(1, data.length)) * (idx + 0.5),
            y: 100 - (pct * 70 + 15)
        };
    });

    const buildSmoothPath = (pts) => {
        if (pts.length === 0) return "";
        if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
        const tension = 0.25;

        let path = `M ${pts[0].x} ${pts[0].y}`;

        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[Math.max(0, i - 1)];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[Math.min(pts.length - 1, i + 2)];

            const cp1x = p1.x + (p2.x - p0.x) * tension;
            const cp1y = p1.y + (p2.y - p0.y) * tension;
            const cp2x = p2.x - (p3.x - p1.x) * tension;
            const cp2y = p2.y - (p3.y - p1.y) * tension;
            const cp2x_val = p2.x;
            const cp2y_val = p2.y;

            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${cp2x_val} ${cp2y_val}`;
        }

        return path;
    };

    const smoothPath = buildSmoothPath(points);
    const areaPath = points.length > 0
        ? `${smoothPath} L ${points[points.length - 1].x} 100 L ${points[0].x} 100 Z`
        : "";

    return (
        <div 
            style={{ 
                borderRadius: "16px", 
                border: "2px solid #482712", 
                boxShadow: "0 15px 40px rgba(72, 39, 18, 0.06)",
                background: "#fff",
                fontFamily: "'Outfit', 'Inter', sans-serif",
                overflow: "hidden"
            }}
            className="mb-3"
        >
            <div style={{ overflowX: "auto", overflowY: "hidden", padding: "20px" }} className="custom-scroll">
                <div style={{ height: `${height}px`, display: "flex", alignItems: "flex-end", position: "relative" }}>
                    <div style={{ height: "100%", position: "relative", minWidth: "450px", width: "100%" }}>
                        
                        {/* Eixos Grid de Fundo */}
                        <svg 
                            style={{ 
                                position: "absolute", 
                                top: 0, 
                                left: 0, 
                                width: "100%", 
                                height: "calc(100% - 30px)", 
                                pointerEvents: "none" 
                            }}
                            preserveAspectRatio="none"
                            viewBox="0 0 100 100"
                        >
                            {[20, 40, 60, 80].map((yVal) => (
                                <line
                                    key={yVal}
                                    x1="0"
                                    y1={yVal}
                                    x2="100"
                                    y2={yVal}
                                    stroke="rgba(72, 39, 18, 0.05)"
                                    strokeWidth="1"
                                    vectorEffect="non-scaling-stroke"
                                />
                            ))}
                        </svg>

                        {/* O SVG de Linhas e Curvas */}
                        <svg 
                            style={{ 
                                position: "absolute", 
                                top: 0, 
                                left: 0, 
                                width: "100%", 
                                height: "calc(100% - 30px)", 
                                pointerEvents: "none",
                                zIndex: 4 
                            }}
                            preserveAspectRatio="none"
                            viewBox="0 0 100 100"
                        >
                            <defs>
                                <linearGradient id={`grad-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={color} stopOpacity="0.25" />
                                    <stop offset="100%" stopColor={color} stopOpacity="0.01" />
                                </linearGradient>
                                <filter id={`shadow-${color.replace("#", "")}`} x="-20%" y="-20%" width="140%" height="140%">
                                    <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor={color} floodOpacity="0.3" />
                                </filter>
                            </defs>
                            {/* Area sob a curva */}
                            {areaPath && (
                                <path
                                    d={areaPath}
                                    fill={`url(#grad-${color.replace("#", "")})`}
                                />
                            )}
                            {/* Linha Curva principal */}
                            {smoothPath && (
                                <path
                                    d={smoothPath}
                                    fill="none"
                                    stroke={color}
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    vectorEffect="non-scaling-stroke"
                                    filter={`url(#shadow-${color.replace("#", "")})`}
                                />
                            )}
                        </svg>
                        
                        {/* Eixo X interativo de Colunas Invisíveis para Trigger de Hover */}
                        <div className="h-100 w-100 d-flex position-relative align-items-end" style={{ zIndex: 1, paddingBottom: "30px" }}>
                            {data.map((item, idx) => {
                                const isHovered = hoveredIdx === idx;
                                const pt = points[idx];
                                const currentY = pt ? 100 - pt.y : 0;
                            
                                return (
                                    <div 
                                        key={idx} 
                                        className="flex-grow-1 h-100 d-flex flex-column align-items-center justify-content-end position-relative"
                                        onMouseEnter={() => setHoveredIdx(idx)}
                                        onMouseLeave={() => setHoveredIdx(null)}
                                        style={{ cursor: "crosshair", zIndex: 5 }}
                                    >
                                        {/* Linha Vertical Tracejada ao Hover */}
                                        {isHovered && (
                                            <div 
                                                className="position-absolute"
                                                style={{ 
                                                    height: "calc(100% - 30px)", 
                                                    width: "1.5px", 
                                                    borderLeft: "1.5px dashed rgba(72, 39, 18, 0.2)", 
                                                    bottom: "30px",
                                                    left: "50%",
                                                    transform: "translateX(-50%)",
                                                    pointerEvents: "none",
                                                    zIndex: 0 
                                                }}
                                            />
                                        )}

                                        {/* Pontos de Destaque Flutuantes sobre a Curva */}
                                        {isHovered && pt && (
                                            <div
                                                className="position-absolute shadow"
                                                style={{
                                                    bottom: `calc(${currentY}% * (1 - 30px / 100%) + 30px)`,
                                                    width: "12px",
                                                    height: "12px",
                                                    borderRadius: "50%",
                                                    left: "50%",
                                                    backgroundColor: "#fff",
                                                    border: `3.5px solid ${color}`,
                                                    transform: "translate(-50%, 50%)",
                                                    zIndex: 20,
                                                    pointerEvents: "none"
                                                }}
                                            />
                                        )}

                                        {/* Card Tooltip suspenso */}
                                        {isHovered && (
                                            <div 
                                                className="position-absolute bg-white shadow-lg rounded-3 p-2 text-center"
                                                style={{ 
                                                    top: "-15px", 
                                                    width: "120px",
                                                    left: "50%", 
                                                    transform: "translateX(-50%)",
                                                    pointerEvents: "none",
                                                    zIndex: 9999,
                                                    border: "2px solid #482712",
                                                    boxShadow: "0 8px 24px rgba(72, 39, 18, 0.15)"
                                                }}
                                            >
                                                <div className="fw-bold mb-1 pb-1 border-bottom text-uppercase font-size-10" style={{ color: "#482712" }}>
                                                    {item.label}
                                                </div>
                                                <div className="d-flex justify-content-center align-items-center">
                                                    <span className="rounded-circle me-1" style={{ width: "6px", height: "6px", backgroundColor: color }} />
                                                    <span className="fw-bold font-size-12" style={{ color: "#482712" }}>
                                                        {item.displayValue}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Mês no eixo X */}
                                        <span 
                                            className="position-absolute fw-bold transition-all" 
                                            style={{ 
                                                bottom: "-22px", 
                                                fontSize: "10px", 
                                                color: isHovered ? color : "#784b22",
                                                opacity: isHovered ? 1 : 0.75
                                            }}
                                        >
                                            {item.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Formata o valor bruto de uma métrica para display humano.
 */
function formatMetricValue(key, value) {
    if (key === "revenue" || key === "revenueWeighted" || key === "ticketMedio") {
        return `R$ ${Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 0 })}`;
    }
    if (
        key.includes("Rate") ||
        key === "assiduidade" ||
        key.includes("conversion") ||
        key.includes("Conversion") ||
        key === "churnRate" ||
        key === "ocupacaoTurmas" ||
        key === "trialConversion" ||
        key === "carteiraHealth"
    ) {
        return `${Number(value).toFixed(1)}%`;
    }
    return String(Math.round(value * 10) / 10);
}

/**
 * Retorna a label curta do mês a partir de um period "YYYY-MM".
 */
function monthLabel(period) {
    if (!period) return "";
    const [yearStr, monthStr] = period.split("-");
    const monthNames = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
    const monthIdx = parseInt(monthStr, 10) - 1;
    return `${monthNames[monthIdx] || "?"}/${yearStr.slice(2)}`;
}

/**
 * Retorna a cor associada a um valor normalizado (0-100).
 */
function getMetricColor(normalized) {
    if (normalized >= 80) return "#34c38f";
    if (normalized >= 50) return "#556ee6";
    if (normalized >= 30) return "#f1b44c";
    return "#f46a6a";
}

/**
 * EmployeeEvolutionPanel — Painel de evolução por colaborador.
 * Exibido em um modal premium. Apresenta o gráfico de evolução do Score Final e
 * tabs para métricas individuais com o gráfico correspondente no estilo da Taça Outono.
 */
const EmployeeEvolutionPanel = ({ employee }) => {
    const { history, loading } = useEmployeeHistory(employee?.idEmployee);
    const [activeTab, setActiveTab] = useState("scoreFinal");

    const metricGroup = employee?.employeeRole || "professor";
    const weights = useMemo(() => DEFAULT_WEIGHTS[metricGroup] || {}, [metricGroup]);
    const metricKeys = useMemo(() => Object.keys(weights), [weights]);

    // Tab options: Score Final + cada métrica individual
    const tabs = useMemo(() => {
        const items = [
            { key: "scoreFinal", label: "Score Final", icon: "mdi-star-circle", color: "#e2661c" },
        ];

        metricKeys.forEach(key => {
            items.push({
                key,
                label: SHORT_LABELS[key] || METRIC_LABELS[key] || key,
                fullLabel: METRIC_LABELS[key] || key,
                icon: "mdi-chart-line",
                color: getMetricColor(employee?.scoreBreakdown?.[key]?.normalized || 0),
                weight: weights[key],
            });
        });

        return items;
    }, [metricKeys, weights, employee]);

    // Filtrar histórico pelo mesmo cargo atual.
    const filteredHistory = useMemo(() => {
        if (!history || history.length === 0) return [];
        return history.filter(snap => snap.employeeRole === metricGroup);
    }, [history, metricGroup]);

    // Dados do gráfico para a tab selecionada
    const chartData = useMemo(() => {
        if (filteredHistory.length === 0) return [];

        if (activeTab === "scoreFinal") {
            return filteredHistory.map(snap => ({
                label: monthLabel(snap.period),
                value: snap.scoreFinal || 0,
                displayValue: (snap.scoreFinal || 0).toFixed(1),
            }));
        }

        return filteredHistory.map(snap => ({
            label: monthLabel(snap.period),
            value: snap.metrics?.[activeTab] || 0,
            displayValue: formatMetricValue(activeTab, snap.metrics?.[activeTab] || 0),
        }));
    }, [filteredHistory, activeTab]);

    // Valor atual (último snapshot ou do employee passado)
    const currentValue = useMemo(() => {
        if (activeTab === "scoreFinal") {
            return { raw: employee?.scoreFinal || 0, normalized: null, display: (employee?.scoreFinal || 0).toFixed(1) };
        }
        const bd = employee?.scoreBreakdown?.[activeTab];
        const rawVal = employee?.metrics?.[activeTab] || bd?.raw || 0;
        return {
            raw: rawVal,
            normalized: bd?.normalized || 0,
            display: formatMetricValue(activeTab, rawVal),
        };
    }, [activeTab, employee]);

    // Variação entre os 2 últimos meses (do mesmo cargo)
    const trend = useMemo(() => {
        if (filteredHistory.length < 2) return null;
        const last = filteredHistory[filteredHistory.length - 1];
        const prev = filteredHistory[filteredHistory.length - 2];

        let lastVal, prevVal;
        if (activeTab === "scoreFinal") {
            lastVal = last.scoreFinal || 0;
            prevVal = prev.scoreFinal || 0;
        } else {
            lastVal = last.metrics?.[activeTab] || 0;
            prevVal = prev.metrics?.[activeTab] || 0;
        }

        const diff = lastVal - prevVal;
        return { diff, direction: diff > 0 ? "up" : diff < 0 ? "down" : "neutral" };
    }, [filteredHistory, activeTab]);

    const activeTabData = tabs.find(t => t.key === activeTab) || tabs[0];

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner size="sm" color="primary" />
                <small className="text-muted ms-2">Carregando histórico...</small>
            </div>
        );
    }

    return (
        <div>
            {/* ═══ TABS DE MÉTRICAS ═══ */}
            <div style={{ overflowX: "auto", whiteSpace: "nowrap" }} className="mb-3 border-bottom pb-2">
                <Nav tabs className="status-tabs-nav border-0 d-flex flex-nowrap">
                    {tabs.map(tab => (
                        <NavItem key={tab.key}>
                            <NavLink
                                className={`status-tab-link ${activeTab === tab.key ? "active" : ""}`}
                                onClick={() => setActiveTab(tab.key)}
                                style={{ cursor: "pointer", padding: "8px 14px", border: "none" }}
                            >
                                <i className={`mdi ${tab.icon} me-1`}></i>
                                {tab.label}
                                {tab.weight && (
                                    <span className="ms-1 opacity-75 font-size-10">
                                        ({Math.round(tab.weight * 100)}%)
                                    </span>
                                )}
                            </NavLink>
                        </NavItem>
                    ))}
                </Nav>
            </div>

            <div className="pt-2">
                {/* ═══ VALOR ATUAL + TREND ═══ */}
                <div className="d-flex align-items-center gap-3 mb-4">
                    <div>
                        <div className="text-muted font-size-10 text-uppercase fw-bold">{activeTabData.fullLabel || activeTabData.label}</div>
                        <h4 className="mb-0 fw-bold" style={{ color: activeTabData.color }}>
                            {currentValue.display}
                        </h4>
                        {currentValue.normalized !== null && (
                            <small className="text-muted font-size-11">Score normalizado: {currentValue.normalized} pts</small>
                        )}
                    </div>
                    {trend && (
                        <div className={`d-flex align-items-center ${trend.direction === "up" ? "text-success" : trend.direction === "down" ? "text-danger" : "text-muted"}`}>
                            <i className={`mdi ${trend.direction === "up" ? "mdi-trending-up" : trend.direction === "down" ? "mdi-trending-down" : "mdi-minus"} font-size-20 me-1`}></i>
                            <span className="font-size-12 fw-bold">
                                {trend.direction === "up" ? "+" : ""}{activeTab === "scoreFinal" ? trend.diff.toFixed(1) : formatMetricValue(activeTab, Math.abs(trend.diff))}
                            </span>
                            <small className="text-muted ms-1">vs mês anterior</small>
                        </div>
                    )}
                </div>

                {/* ═══ GRÁFICO ═══ */}
                <EvolutionLineChart
                    data={chartData}
                    color={activeTabData.color}
                    height={200}
                />

                {filteredHistory.length === 0 && (
                    <div className="text-center text-muted py-4">
                        <i className="mdi mdi-chart-timeline-variant-shimmer font-size-24" style={{ color: "#784b22" }}></i>
                        <p className="mt-2 font-size-12 mb-0" style={{ color: "#784b22" }}>
                            {history.length > 0 && history.length !== filteredHistory.length
                                ? "Este colaborador mudou de cargo. Mostrando apenas dados do cargo atual."
                                : "O histórico de evolução aparecerá aqui após o fechamento do primeiro mês."
                            }
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

EmployeeEvolutionPanel.propTypes = {
    employee: PropTypes.object,
};

export default EmployeeEvolutionPanel;
